import React, { memo, useCallback } from 'react';
import { FormSelect, FormSelectItem } from '@core/components/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { Button } from '@core/components/Button';
import { SortableList } from '@modules/common/Sortable';
import { SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { ProjectListCategoryForm } from '../ProjectListBlock';
import { ProjectListNodeType } from '../types';
import { ProjectListNodeItem } from './ProjectListNodeItem';

interface ProjectListCategoryContentProps {
  categoryCtrl: ProjectListCategoryForm;
  categoryIndex: number;
  categoryItems: FormSelectItem<any, any>[];
}

const NODE_BUTTONS: { label: string; type: ProjectListNodeType }[] = [
  { label: 'Text', type: ProjectListNodeType.text },
  { label: 'Calendar', type: ProjectListNodeType.calendarEvent },
  { label: 'Vendors', type: ProjectListNodeType.vendors },
  { label: 'VCC Items', type: ProjectListNodeType.vccItems },
];

export const ProjectListCategoryContent = memo((props: ProjectListCategoryContentProps) => {
  const { categoryCtrl, categoryIndex, categoryItems } = props;
  const categoryValue = useNonNilObservable(categoryCtrl.value$);
  const nodeControls = useNonNilObservable(categoryCtrl.controls.nodes.items$);

  const addNode = useCallback(
    (type: ProjectListNodeType) => {
      const ctrl = categoryCtrl.controls.nodes.push();
      // Seed the Calendar node with the {{date}} token so it's editable text the author can
      // type around (e.g. "Need on: {{date}}").
      ctrl.patchValue({ type, ...(type === ProjectListNodeType.calendarEvent ? { content: '{{date}}' } : {}) });
    },
    [categoryCtrl],
  );

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      categoryCtrl.patchValue({ nodes: R.move(categoryValue.nodes, sort.oldIndex, sort.newIndex) });
    },
    [categoryCtrl, categoryValue.nodes],
  );

  return (
    <div>
      <FormSelect control={categoryCtrl.controls.categoryId} items={categoryItems} label={`Category ${categoryIndex + 1}`} />

      <div className="d-flex align-items-center flex-wrap mt-3 mb-2" style={{ gap: 8 }}>
        <span className="font-weight-bold mr-2">Nodes</span>
        {NODE_BUTTONS.map(b => (
          <Button key={b.type} color="primary" outline size="small" onClick={() => addNode(b.type)}>
            + {b.label}
          </Button>
        ))}
      </div>

      <SortableList useDragHandle lockAxis="y" onSortEnd={onSortEnd}>
        {nodeControls.map((nodeCtrl, index) => (
          <ProjectListNodeItem key={index} index={index} nodeCtrl={nodeCtrl} onDelete={() => categoryCtrl.controls.nodes.removeAt(index)} />
        ))}
      </SortableList>
    </div>
  );
});
